import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import Reveal from '../components/Reveal';

const About = () => {
  const differentiators = [
    {
      title: 'Curated access via profiles',
      desc: 'We review every application to ensure community alignment and value fit',
    },
    {
      title: 'Theme-driven networking',
      desc: 'Events designed around specific interests, creating natural conversation starters',
    },
    {
      title: 'Expert-led workshops',
      desc: 'Learn from industry leaders and practitioners at the top of their fields',
    },
    {
      title: 'Balanced lifestyle experiences',
      desc: 'Professional growth meets hobby exploration and meaningful social connections',
    },
  ];

  const steps = [
    {
      n: '01',
      title: 'Apply',
      desc: "Submit your profile sharing your interests, goals, and what you're looking to gain from the community",
    },
    {
      n: '02',
      title: 'Get Approved',
      desc: "Our team reviews applications to ensure community fit and value alignment. We're building for quality.",
    },
    {
      n: '03',
      title: 'Attend & Grow',
      desc: 'Access curated events, connect with like-minded individuals, and accelerate your personal and professional growth',
    },
  ];

  return (
    <div className="dark-container" data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-radial-teal opacity-50 pointer-events-none" />
        <div className="dark-content-container relative pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="max-w-4xl">
            <span className="eyebrow mb-6 block animate-fade-in">About us</span>
            <h1 className="display-huge text-white animate-fade-in-delay-1" data-testid="about-title">
              About <span className="text-[#64C8FF]">Deft</span><br />Experiences
            </h1>
          </div>
        </div>
      </section>

      {/* 2-COL INTRO */}
      <section className="relative">
        <div className="dark-content-container pb-24 lg:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7 space-y-10">
              <Reveal as="p" className="text-2xl md:text-3xl text-white font-light leading-snug font-display">
                Deft Experiences curates invite-only events uniting bright, ambitious individuals
                for purposeful connections, skill-building, and exploration. From professional
                networking to hobby discovery and anti-brainrot learning via The Nerd Feed, we
                design depth over volume.
              </Reveal>

              <div className="space-y-4 pt-4">
                {differentiators.map((d, i) => (
                  <Reveal
                    key={i}
                    delay={i * 90}
                    className="group flex items-start gap-5 p-6 surface-2 border border-white/8 rounded-2xl dark-transition hover:border-[#00FFD1]/30"
                    data-testid={`about-differentiator-${i}`}
                  >
                    <div className="w-10 h-10 rounded-xl glass-teal flex items-center justify-center flex-shrink-0">
                      <Check className="text-[#00FFD1]" size={18} />
                    </div>
                    <div>
                      <h3 className="heading-3 text-white mb-1.5">{d.title}</h3>
                      <p className="body-small text-white/65">{d.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal className="pt-4">
                <Link to="/events" className="btn-primary inline-flex" data-testid="about-explore-events-btn">
                  Explore Events
                  <ArrowUpRight size={18} />
                </Link>
              </Reveal>
            </div>

            <Reveal delay={150} className="lg:col-span-5 relative lg:sticky lg:top-28">
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_deftadmin/artifacts/taguwxqz_IMG-20251122-WA0019.jpg"
                  alt="Deft Experiences Event"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 glass-panel rounded-2xl p-5">
                  <p className="body-small text-[#00FFD1] mb-1">A glimpse</p>
                  <p className="heading-3 text-white">Where ambition meets community</p>
                </div>
              </div>
              <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-[#00FFD1]/40 rounded-tl-3xl pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-[#64C8FF]/40 rounded-br-3xl pointer-events-none" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative surface-1 border-y border-white/5">
        <div className="absolute inset-0 bg-grid-pattern-fine opacity-50 pointer-events-none" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-radial-teal opacity-40 pointer-events-none" />
        <div className="dark-content-container relative py-24 lg:py-36">
          <Reveal className="max-w-4xl mx-auto text-center">
            <span className="eyebrow mb-6 block">Our north star</span>
            <h2 className="display-large text-white mb-8">Our Mission</h2>
            <p className="text-xl md:text-2xl text-white/80 font-light leading-snug font-display">
              To create high-signal environments where ambitious individuals can forge meaningful
              connections, accelerate their growth, and explore new dimensions of their potential.
              We believe in quality over quantity, depth over breadth, and authentic relationships
              over superficial networking.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW DEFT WORKS */}
      <section className="relative">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="max-w-3xl mb-16">
            <span className="eyebrow mb-4 block">Process</span>
            <h2 className="display-large text-white">How Deft Works</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <Reveal
                key={i}
                delay={i * 130}
                className="surface-2 border border-white/8 rounded-3xl p-10 dark-transition hover:border-[#00FFD1]/30 relative overflow-hidden h-full"
                data-testid={`how-step-${i}`}
              >
                <p className="text-7xl font-light text-[#00FFD1]/30 mb-6 font-display leading-none">{s.n}</p>
                <h3 className="heading-1 text-white mb-3">{s.title}</h3>
                <p className="body-medium text-white/65">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="relative overflow-hidden surface-2 border border-white/10 rounded-[2.5rem] p-12 md:p-20 text-center">
            <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-radial-blue opacity-50 pointer-events-none" />
            <div className="relative">
              <span className="eyebrow mb-6 block">Membership</span>
              <h2 className="display-large text-white mb-6">Join the Community</h2>
              <p className="body-large text-white/70 mb-10 max-w-2xl mx-auto">
                Start your journey with Deft Experiences. Register your interest and connect with
                ambitious individuals building their best lives.
              </p>
              <a
                href="https://deftexp.fillout.com/lessgo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex"
                data-testid="about-cta-register-btn"
              >
                Register Your Interest
                <ArrowUpRight size={18} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
